export const ProcessStatus = {
  Pending: "PENDING",
  BatchCreating: "BATCH_CREATING",
  BatchCreated: "BATCH_CREATED",
  Processing: "PROCESSING",
  PartialCompleted: "PARTIAL_COMPLETED",
  Completed: "COMPLETED",
  Failed: "FAILED",
  EvaluationFinished: "EVALUATION_FINISHED",
} as const;

export type ProcessStatus = (typeof ProcessStatus)[keyof typeof ProcessStatus];

export const ProcessItemStatus = {
  Pending: "PENDING",
  Created: "CREATED",
  OcrExecution: "OCR_EXECUTION",
  PageDetection: "PAGE_DETECTION",
  DataExtraction: "DATA_EXTRACTION",
  ResultMerging: "RESULT_MERGING",
  Completed: "COMPLETED",
  Skipped: "SKIPPED",
  Failed: "FAILED",
} as const;

export type ProcessItemStatus =
  (typeof ProcessItemStatus)[keyof typeof ProcessItemStatus];

export const ExtractionViewStatus = {
  Processing: "processing",
  Completed: "completed",
  Failed: "failed",
} as const;

export type ExtractionViewStatus =
  (typeof ExtractionViewStatus)[keyof typeof ExtractionViewStatus];

export const SupportedDownloadFileType = {
  Excel: "excel",
  Json: "json",
} as const;

export type SupportedDownloadFileType =
  (typeof SupportedDownloadFileType)[keyof typeof SupportedDownloadFileType];

export const WebSocketMessageType = {
  Error: "error",
  Ping: "ping",
  Pong: "pong",
  StatusUpdate: "status_update",
  Authentication: "authentication",
  ConnectionEstablished: "connection_established",
  AuthenticationSuccess: "authentication_success",
  AuthenticationFailed: "authentication_failed",
  AuthenticationTimeout: "authentication_timeout",
} as const;

export type WebSocketMessageType =
  (typeof WebSocketMessageType)[keyof typeof WebSocketMessageType];

// ========== NOTIFICATION MODELS ==========
export interface INotificationMessage {
  type: string;
  message?: string;
  status?: ProcessStatus;
  batch_item_id?: string;
  batch_item_status?: string;
  process_id?: string;
  timestamp: string;
}

export interface INotificationMessageViewModel {
  type: string;
  message?: string;
  status?: ProcessStatus;
  batchItemId?: string;
  batchItemStatus?: string;
  processId?: string;
  timestamp: string;
}

// ========== RESPONSE MODELS (from API) ==========
export interface IPresignedUrlResponseModel {
  id: string;
  upload_url: string;
  expires_in: number;
}

export interface IDisplayImageModel {
  page_number: string;
  url: string;
}

export interface IBoundingBox {
  top: number;
  left: number;
  bottom: number;
  right: number;
}

export interface IExtractedItemResponse {
  id: string;
  key: string;
  value: string;
  page: number;
  uom?: string | null;
  conf?: number | null;
  bbox?: IBoundingBox | null;
}

export interface IExtractedNodeReponse {
  id: string;
  name: string;
  display_name: string;
  level: number;
  is_expandable: boolean;
  children: IExtractedNodeReponse[];
  data?: IExtractedItemResponse | null;
}

export interface IExtractedResultModel {
  display_nodes: IExtractedNodeReponse[];
  error_message?: string | null;
  preview_url: string | null;
  no_pages?: number | null;
  is_pdf?: boolean | null;
}

export interface IDownloadedFileResponseModel {
  file_name: string;
  s3_key: string;
}

// Process Item for individual files within a process
export interface IProcessItemModel {
  id: string;
  file_name: string;
  status: string;
  demo_image: string | null;
  created_at: string;
  error_message: string | null;
}

// Process Response Info for processed results list
export interface IProcessInfoModel {
  id: string;
  status: string;
  created_at: string;
  is_batch: boolean;
  error_message: string | null;
  items: IProcessItemModel[];
}

// ========== VIEW MODELS (for UI) ==========
export interface IDisplayImageViewModel {
  pageNumber: string;
  url: string;
}

export interface IPresignedUrlViewModel {
  id: string;
  uploadUrl: string;
  expiresIn: number;
}

export interface IExtractedItemViewModel {
  id: string;
  key: string;
  value: string;
  page: number;
  uom?: string | null;
  bbox?: IBoundingBox | null;
  conf?: number | null;
}

export interface IExtractedNodeViewModel {
  id: string;
  name: string;
  displayName: string;
  level: number;
  isExpandable: boolean;
  children: IExtractedNodeViewModel[];
  data?: IExtractedItemViewModel | null;
}

export interface IExtractedResultViewModel {
  displayNode: IExtractedNodeViewModel[];
  errorMessage?: string | null;
  previewUrl: string | null;
  isPdf: boolean;
  noPages: number;
}

// Process Item ViewModel for individual files within a process
export interface IProcessItemViewModel {
  id: string;
  fileName: string;
  status: string;
  demoImage: string | null;
  createdAt: string;
  errorMessage: string | null;
}

// Process Info ViewModel for processed results list
export interface IProcessInfoViewModel {
  id: string;
  status: string;
  createdAt: string;
  isBatch: boolean;
  errorMessage: string | null;
  items: IProcessItemViewModel[];
}

export interface IBoundingBoxData {
  bbox: IBoundingBox;
  label: string;
}

export interface IDownloadedFileViewModel {
  fileName: string;
  file: Blob;
}

// ========== FILTER MODELS ==========
export interface IProcessedResultsFilterRequest {
  start_date?: string | null;
  end_date?: string | null;
  file_name?: string | null;
  user_email?: string | null;
}

export interface IProcessedResultsFilterViewModel {
  startDate?: string | null;
  endDate?: string | null;
  fileName?: string | null;
  userEmail?: string | null;
}

// ========== PROCESS DETAIL MODELS (for monitoring with items) ==========
export interface IProcessItemDetailModel {
  id: string;
  file_name: string;
  status: string;
  error_message: string | null;
  created_at: string;
}

export interface IProgressModel {
  total: number;
  completed: number;
}

export interface IUserInputModel {
  [key: string]: string;
}

export interface IUserInputViewModel {
  pageNumbers: number[];
  instructions: string;
}

export interface IProcessDetailResponse {
  id: string;
  status: string;
  progress: IProgressModel;
  is_batch: boolean;
  start_at: string | null;
  finished_at: string | null;
  error_message: string | null;
  user_input: IUserInputModel;
  process_items: IProcessItemDetailModel[];
}

export interface IProcessItemDetailViewModel {
  id: string;
  idShort: string;
  fileName: string;
  status: string;
  errorMessage: string | null;
  createdAt: string;
}

export interface IProcessDetailViewModel {
  id: string;
  status: string;
  progress: {
    total: number;
    completed: number;
  };
  isBatch: boolean;
  startAt: string | null;
  finishedAt: string | null;
  errorMessage: string | null;
  userInput: IUserInputViewModel;
  processItems: IProcessItemDetailViewModel[];
}
